import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { formatDate, parseNumberString } from "./utils";

class ExportXLSXUseCase {
  async execute(objects1, sales, purchases, titles, filename) {
    try {
      const workbook = new ExcelJS.Workbook();

      /* ===== Sheet 1 ===== */
      const sheet1 = workbook.addWorksheet("Sheet 1");

      sheet1.columns = Object.keys(objects1[0]).map((key) => ({
        header: key,
        key: key,
        width: 20,
      }));

      objects1.forEach((obj) => sheet1.addRow(obj));

      let objectsRow = 2;
      objects1.forEach((obj) => {
        const row = sheet1.getRow(objectsRow);
        row.values = [
          formatDate(obj["Date"]),
          obj["TIN#"],
          obj["Company Name"],
          obj["Address"],
          parseNumberString(obj["Gross"]) || obj["Gross"],
          parseNumberString(obj["Vatable Sales"]) || obj["Vatable Sales"],
          parseNumberString(obj["Vat Amount"]) || obj["Vat Amount"],
        ];
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (typeof cell.value === "number") {
            cell.numFmt = "#,##0.00";
          }
        });
        objectsRow++;
      });

      sheet1.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (typeof cell.value === "number") {
            cell.numFmt = "#,##0.00";
          }
        });
      });

      sheet1.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
      });

      sheet1.getRow(1).height = 30;
      sheet1.getRow(1).eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      sheet1.mergeCells(`A${objectsRow}:D${objectsRow}`);
      sheet1.getCell(`A${objectsRow}`).value = titles.totalTitles;
      sheet1.getCell(`A${objectsRow}`).font = { bold: true };
      sheet1.getCell(`A${objectsRow}`).alignment = { horizontal: "right" };

      sheet1.getCell(`E${objectsRow}`).value = {
        formula: `SUM(E2:E${objectsRow - 1})`,
      };
      sheet1.getCell(`F${objectsRow}`).value = {
        formula: `SUM(F2:F${objectsRow - 1})`,
      };
      sheet1.getCell(`G${objectsRow}`).value = {
        formula: `SUM(G2:G${objectsRow - 1})`,
      };

      ["A", "E", "F", "G"].forEach((col) => {
        const cell = sheet1.getCell(`${col}${objectsRow}`);
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.numFmt = "#,##0.00";
      });

      /* ===== Sheet 2 ===== */
      const sheet2 = workbook.addWorksheet("Sheet 2");

      sheet2.columns = [
        { header: "DATE", key: "date", width: 15 },
        { header: "OR NUMBER", key: "orNumber", width: 15 },
        { header: "TIN taxpayer ID #", key: "tin", width: 20 },
        { header: "RECEIVED FROM", key: "receivedFrom", width: 30 },
        { header: "ADDRESS", key: "address", width: 30 },
        { header: "TOTAL AMOUNT", key: "totalAmount", width: 18 },
        { header: "TAXABLE AMOUNT", key: "taxableAmount", width: 18 },
        { header: "INPUT TAX (12%)", key: "inputTax", width: 18 },
      ];

      sheet2.mergeCells("A1:H1");
      sheet2.getCell("A1").value = "MWEEB INFORMATION TECHNOLOGY";
      sheet2.getCell("A1").font = { bold: true, size: 14 };
      sheet2.getCell("A1").alignment = {
        vertical: "middle",
        horizontal: "left",
      };

      sheet2.mergeCells("A2:H2");
      sheet2.getCell("A2").value = "FOR VAT 2550Q AND ATTACHMENT";
      sheet2.getCell("A2").alignment = { horizontal: "left" };

      sheet2.mergeCells("A4:H4");
      sheet2.getCell("A4").value = "A) SUMMARY LIST OF SALES/SERVICES";
      sheet2.getCell("A4").font = { bold: true };
      sheet2.getCell("A4").alignment = { horizontal: "left" };

      sheet2.mergeCells("A5:H5");
      sheet2.getCell("A5").value = titles.title;
      sheet2.getCell("A5").alignment = { horizontal: "left" };

      const headerRow = sheet2.getRow(7);
      [
        "DATE",
        "OR NUMBER",
        "TIN taxpayer ID #",
        "RECEIVED FROM",
        "ADDRESS",
        "TOTAL AMOUNT",
        "TAXABLE AMOUNT",
        "INPUT TAX (12%)",
      ].forEach((text, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = text;
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
      sheet2.getRow(7).height = 30;
      sheet2.getRow(7).eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      let rowNumber = 8;
      sales.forEach((obj) => {
        const row = sheet2.getRow(rowNumber);
        row.values = [
          formatDate(obj.date),
          obj.orNumber || "",
          obj.client.tinNo,
          obj.client.companyName || obj.client.clientName,
          obj.client.billingCity,
          parseNumberString(obj.gross) || obj.gross,
          parseNumberString(obj.net) || obj.net,
          parseNumberString(obj.vat) || obj.vat,
        ];
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (typeof cell.value === "number") {
            cell.numFmt = "#,##0.00";
          }
        });
        rowNumber++;
      });

      sheet2.mergeCells(`A${rowNumber}:E${rowNumber}`);
      sheet2.getCell(`A${rowNumber}`).value = titles.totalTitles;
      sheet2.getCell(`A${rowNumber}`).font = { bold: true };
      sheet2.getCell(`A${rowNumber}`).alignment = { horizontal: "right" };

      sheet2.getCell(`F${rowNumber}`).value = {
        formula: `SUM(F8:F${rowNumber - 1})`,
      };
      sheet2.getCell(`G${rowNumber}`).value = {
        formula: `SUM(G8:G${rowNumber - 1})`,
      };
      sheet2.getCell(`H${rowNumber}`).value = {
        formula: `SUM(H8:H${rowNumber - 1})`,
      };

      ["A", "F", "G", "H"].forEach((col) => {
        const cell = sheet2.getCell(`${col}${rowNumber}`);
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.numFmt = "#,##0.00";
      });

      const sectionBRow = rowNumber + 2;
      sheet2.mergeCells(`A${sectionBRow}:H${sectionBRow}`);
      sheet2.getCell(`A${sectionBRow}`).value =
        "B) SUMMARY LIST OF PURCHASES (GOODS AND SERVICES) TRANSACTION";
      sheet2.getCell(`A${sectionBRow}`).font = { bold: true };
      sheet2.getCell(`A${sectionBRow}`).alignment = { horizontal: "left" };

      const slspobjectRow = sectionBRow + 3;
      let slspHeaderRow = sectionBRow + 2;
      const slspHeader = sheet2.getRow(slspHeaderRow);
      [
        "DATE",
        "OR NUMBER",
        "TIN taxpayer ID #",
        "PAYEE",
        "ADDRESS",
        "TOTAL AMOUNT",
        "TAXABLE AMOUNT",
        "INPUT TAX (12%)",
      ].forEach((text, i) => {
        const cell = slspHeader.getCell(i + 1);
        cell.value = text;
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      sheet2.getRow(slspHeaderRow).height = 30;
      sheet2.getRow(slspHeaderRow).eachCell((cell) => {
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });

      purchases.forEach((obj) => {
        const row = sheet2.getRow(slspHeaderRow + 1);
        row.values = [
          formatDate(obj.date),
          obj.orNumber || "",
          obj.supplier.tinNo,
          obj.supplier.name,
          obj.supplier.city,
          parseNumberString(obj.gross) || obj.gross,
          parseNumberString(obj.net) || obj.net,
          parseNumberString(obj.vat) || obj.vat,
        ];
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (typeof cell.value === "number") {
            cell.numFmt = "#,##0.00";
          }
        });
        slspHeaderRow++;
      });

      sheet2.mergeCells(`A${slspHeaderRow + 1}:E${slspHeaderRow + 1}`);
      sheet2.getCell(`A${slspHeaderRow + 1}`).value = titles.totalTitles;
      sheet2.getCell(`A${slspHeaderRow + 1}`).font = { bold: true };
      sheet2.getCell(`A${slspHeaderRow + 1}`).alignment = {
        horizontal: "right",
      };

      sheet2.getCell(`F${slspHeaderRow + 1}`).value = {
        formula: `SUM(F${slspobjectRow}:F${slspHeaderRow})`,
      };
      sheet2.getCell(`G${slspHeaderRow + 1}`).value = {
        formula: `SUM(G${slspobjectRow}:G${slspHeaderRow})`,
      };
      sheet2.getCell(`H${slspHeaderRow + 1}`).value = {
        formula: `SUM(H${slspobjectRow}:H${slspHeaderRow})`,
      };

      ["A", "F", "G", "H"].forEach((col) => {
        const cell = sheet2.getCell(`${col}${slspHeaderRow + 1}`);
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.numFmt = "#,##0.00";
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);
    } catch (error) {
      console.error("Error generating Excel:", error);
      throw error;
    }
  }
}

export default ExportXLSXUseCase;
